/**
 * ZIE Travels - Cinematic Day-by-Day Story Scroll Interactions
 * Powered by GSAP and Observer
 * 
 * Re-initializes dynamically on Shopify Customizer section load.
 */

let zieStoryCtx = null;
let zieStoryObserver = null;

function initZieStoryScroll() {
  // Clean up any existing scoped GSAP animations, Observer, and classes
  if (zieStoryCtx) {
    zieStoryCtx.revert();
    zieStoryCtx = null;
  }
  if (zieStoryObserver) {
    zieStoryObserver.kill();
    zieStoryObserver = null;
  }
  document.documentElement.classList.remove("zie-story-template-active");

  const container = document.querySelector('[data-gsap="product-container"]');
  if (!container) return;

  // Respect user prefers-reduced-motion settings
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReduced) {
    console.warn("GSAP: Prefers-reduced-motion matches. Skipping animations.");
    return;
  }

  // Create scoped GSAP context
  zieStoryCtx = gsap.context(() => {

    // Add HTML scroll lock class
    document.documentElement.classList.add("zie-story-template-active");

    const panels = gsap.utils.toArray('[data-gsap="day-panel"]');
    const bgImages = gsap.utils.toArray('[data-gsap="bg-image"]');

    if (panels.length === 0) return;

    let activeIndex = 0;
    let animating = false;

    // Set initial state: Day 1 visible, others hidden
    panels.forEach((panel, idx) => {
      if (idx === 0) {
        gsap.set(panel, { opacity: 1, y: 0, pointerEvents: "all" });
        panel.classList.add("active-panel");
      } else {
        gsap.set(panel, { opacity: 0, y: 20, pointerEvents: "none" });
        panel.classList.remove("active-panel");
      }
    });

    if (bgImages.length > 0) {
      bgImages.forEach((img, idx) => {
        if (idx === 0) {
          img.classList.add("active");
          const targetOpacity = window.innerWidth <= 989 ? 0.25 : 0.35;
          gsap.set(img, { opacity: targetOpacity });
        } else {
          img.classList.remove("active");
          gsap.set(img, { opacity: 0 });
        }
      });
    }

    function gotoDay(index, direction) {
      if (animating || index < 0 || index >= panels.length) return;
      animating = true;

      const isMobile = window.innerWidth <= 989;
      const nextPanelDelay = isMobile ? 0.2 : 0.1;

      const prevPanel = panels[activeIndex];
      const nextPanel = panels[index];
      const prevBg = bgImages[activeIndex];
      const nextBg = bgImages[index];

      const tl = gsap.timeline({
        onComplete: () => {
          activeIndex = index;
          animating = false;
        }
      });

      // 1. Fade out previous panel and its background
      if (prevPanel) {
        gsap.set(prevPanel, { pointerEvents: "none" });
        tl.to(prevPanel, {
          opacity: 0,
          y: direction > 0 ? -20 : 20,
          filter: "blur(0.5px)", // Cinematic blur out
          duration: 0.4,
          ease: "power2.inOut",
          onComplete: () => {
            prevPanel.classList.remove("active-panel");
            gsap.set(prevPanel, { filter: "none" }); // Reset filter for clean state
          }
        }, 0);
      }

      if (prevBg) {
        tl.to(prevBg, {
          opacity: 0,
          duration: 0.5,
          ease: "power2.inOut",
          onComplete: () => {
            prevBg.classList.remove("active");
          }
        }, 0);
      }

      // 2. Fade in next panel and its background
      if (nextPanel) {
        nextPanel.scrollTop = 0; // Reset scroll position of incoming panel
        // Pre-position, hide, and apply starting blur immediately to avoid layout flashing
        gsap.set(nextPanel, { y: 30, opacity: 0, filter: "blur(8px)" });

        // Add class and set pointer events at the exact moment the fade-in starts
        tl.call(() => {
          nextPanel.classList.add("active-panel");
        }, null, nextPanelDelay);
        tl.set(nextPanel, { pointerEvents: "all" }, nextPanelDelay);

        tl.to(nextPanel, {
          opacity: 1,
          y: 0,
          filter: "blur(0px)", // Cinematic blur in
          duration: 0.5,
          ease: "power2.out",
          onComplete: () => {
            gsap.set(nextPanel, { filter: "none" }); // Reset filter to none for crisp text rendering
          }
        }, nextPanelDelay);
      }

      if (nextBg) {
        tl.call(() => {
          nextBg.classList.add("active");
        }, null, 0.1);
        const targetOpacity = window.innerWidth <= 989 ? 0.25 : 0.35;
        tl.to(nextBg, {
          opacity: targetOpacity,
          duration: 0.6,
          ease: "power2.out"
        }, 0.1);
      }
    }

    // Create Observer to listen to wheel, touch scroll intents
    zieStoryObserver = Observer.create({
      target: window,
      type: "wheel,touch,pointer",
      wheelSpeed: 0.8,
      tolerance: 15,
      preventDefault: false, // Allow elements inside to scroll natively
      onUp: (self) => {
        const panel = panels[activeIndex];
        const isTouch = self.event && (
          self.event.type.includes('touch') ||
          self.event.pointerType === 'touch'
        );

        if (isTouch) {
          // Swipe UP (fires onUp) -> go FORWARD (next day)
          const canScrollDown = panel && (panel.scrollTop + panel.clientHeight < panel.scrollHeight - 2);
          if (!canScrollDown && activeIndex < panels.length - 1) {
            gotoDay(activeIndex + 1, 1);
          }
        } else {
          // Wheel UP -> go BACKWARD (previous day)
          const canScrollUp = panel && panel.scrollTop > 2;
          if (!canScrollUp && activeIndex > 0) {
            gotoDay(activeIndex - 1, -1);
          }
        }
      },
      onDown: (self) => {
        const panel = panels[activeIndex];
        const isTouch = self.event && (
          self.event.type.includes('touch') ||
          self.event.pointerType === 'touch'
        );

        if (isTouch) {
          // Swipe DOWN (fires onDown) -> go BACKWARD (previous day)
          const canScrollUp = panel && panel.scrollTop > 2;
          if (!canScrollUp && activeIndex > 0) {
            gotoDay(activeIndex - 1, -1);
          }
        } else {
          // Wheel DOWN -> go FORWARD (next day)
          const canScrollDown = panel && (panel.scrollTop + panel.clientHeight < panel.scrollHeight - 2);
          if (!canScrollDown && activeIndex < panels.length - 1) {
            gotoDay(activeIndex + 1, 1);
          }
        }
      }
    });

    // Client-side HTML tag unescaping fallback script
    document.querySelectorAll('.zie-day-desc, .zie-day-highlights .rte, .day-desc, .highlight-val').forEach(el => {
      const txt = el.textContent || el.innerText;
      if (txt.includes('<div') || txt.includes('<p') || txt.includes('<h') || txt.includes('<ul') || txt.includes('<li')) {
        el.innerHTML = txt;
      }
    });

  }, container);
}

// Function to handle loading race conditions for deferred scripts
function checkGSAPAndInit() {
  if (typeof gsap !== "undefined" && typeof Observer !== "undefined") {
    gsap.registerPlugin(Observer);
    initZieStoryScroll();
  } else {
    // Retry in 50ms if libraries are not loaded yet
    setTimeout(checkGSAPAndInit, 50);
  }
}

// Initial launch trigger
checkGSAPAndInit();

// Re-initialize inside Shopify Customizer when sections load/reload
document.addEventListener("shopify:section:load", (event) => {
  if (event.target.querySelector('[data-gsap="product-container"]')) {
    checkGSAPAndInit();
  }
});
